import { TransactionType } from "@core/enums/transaction-type";
import { TransactionHelper } from "@core/helpers/transaction-helper";
import { BankBatch } from "@core/models/bank-batch-model";
import { BankCollectionBatch } from "@core/models/bank-collection-batch";
import { CollectionTransaction } from "@core/models/collection-transaction.model";
import { ExtendedGfhAccount } from "@core/models/extended-gfh-account.model";
import { PendingTransaction } from "@core/models/pending-transaction.model";
import { Search } from "@core/models/search.model";
import { Transaction } from "@core/models/transaction.model";
import { FundWithRelated } from "@models/fund-with-related.model";
import { createFeatureSelector, createSelector } from "@ngrx/store";

import * as fromTransaction from "./transaction.reducers";
import { TransactionState } from "./transaction.reducers";

export const SELECT_TRANSACTION_STATE =
  createFeatureSelector<fromTransaction.TransactionState>(
    fromTransaction.TRANSACTION_FEATURE_KEY
  );

export const SELECT_BANK_BATCH_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<BankBatch> => state.bankBatches
);

export const SELECT_TRANSACTION_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<Transaction> => state.transactions
);

export const SELECT_UNMATCHED_TRANSACTION_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<Transaction> =>
    state.transactions.filter((transaction: Transaction): boolean =>
      TransactionHelper.isUnmatchedTransaction(transaction)
    )
);

export const SELECT_SUPPRESSED_TRANSACTION_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<Transaction> =>
    state.transactions.filter(
      (transaction: Transaction): boolean => transaction.suppressed
    )
);

export const SELECT_TRANSACTION_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number => state.transactions.length
);

export const SELECT_UNMATCHED_TRANSACTION_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number =>
    state.transactions.filter((transaction: Transaction): boolean =>
      TransactionHelper.isUnmatchedTransaction(transaction)
    ).length
);

export const SELECT_SUPPRESSED_TRANSACTION_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number =>
    state.transactions.filter(
      (transaction: Transaction): boolean => transaction.suppressed
    ).length
);

export const SELECT_TRANSACTION_SEARCH_ITEMS = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Search | null => state.searchItems
);

export const SELECT_TRANSACTION_LOADING_STATUS = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): boolean => state.loading
);

export const SELECT_GFH_ACCOUNT_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<ExtendedGfhAccount> => state.gfhAccounts
);

export const SELECT_BANK_COLLECTION_BATCH_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<BankCollectionBatch> =>
    state.bankCollectionBatches
);

export const SELECT_BANK_RETRY_COLLECTION_BATCH_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<BankCollectionBatch> =>
    state.bankRetryCollectionBatches
);

export const SELECT_BANK_FAILED_COLLECTION_BATCH_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<CollectionTransaction> =>
    state.bankFailedCollectionBatches
);

export const SELECT_COLLECTION_LOADING_STATUS = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): boolean => state.collectionLoading
);

export const SELECT_RETRY_COLLECTION_LOADING_STATUS = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): boolean => state.retryCollectionLoading
);

export const SELECT_FAILED_COLLECTION_LOADING_STATUS = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): boolean => state.failedCollectionLoading
);

export const SELECT_BANK_COLLECTION_BATCH_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number =>
    state.bankCollectionBatches.filter(
      (batch) => !batch.collectionBatch?.definitive
    ).length
);

export const SELECT_BANK_RETRY_COLLECTION_BATCH_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number =>
    state.bankRetryCollectionBatches.filter(
      (batch) => !batch.collectionBatch?.definitive
    ).length
);

export const SELECT_FUND_WITH_LATEST_NAV = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<FundWithRelated> => state.funds
);

export const SELECT_DISBURSEMENT_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<PendingTransaction> => state.disbursementList
);

export const SELECT_DISBURSEMENT_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number => state.disbursementList.length
);

export const SELECT_DISBURSEMENT_LOADING_STATUS = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): boolean => state.disbursementLoading
);

export const SELECT_PERIODIC_DISBURSEMENT_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<PendingTransaction> =>
    state.disbursementList.filter(
      (disbursement: PendingTransaction): boolean =>
        disbursement.transactionType === TransactionType.DisbursementPeriodic
    )
);

export const SELECT_PERIODIC_DISBURSEMENT_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number =>
    state.disbursementList.filter(
      (disbursement: PendingTransaction): boolean =>
        disbursement.transactionType === TransactionType.DisbursementPeriodic
    ).length
);

export const SELECT_WITHDRAWAL_DISBURSEMENT_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<PendingTransaction> =>
    state.disbursementList.filter(
      (disbursement: PendingTransaction): boolean =>
        disbursement.transactionType ===
        TransactionType.DisbursementManualWithdrawal
    )
);

export const SELECT_WITHDRAWAL_DISBURSEMENT_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number =>
    state.disbursementList.filter(
      (disbursement: PendingTransaction): boolean =>
        disbursement.transactionType ===
        TransactionType.DisbursementManualWithdrawal
    ).length
);

export const SELECT_CLOSING_DISBURSEMENT_LIST = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): Array<PendingTransaction> =>
    state.disbursementList.filter(
      (disbursement: PendingTransaction): boolean =>
        disbursement.transactionType ===
        TransactionType.DisbursementAccountClosure
    )
);

export const SELECT_CLOSING_DISBURSEMENT_LIST_LENGTH = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): number =>
    state.disbursementList.filter(
      (disbursement: PendingTransaction): boolean =>
        disbursement.transactionType ===
        TransactionType.DisbursementAccountClosure
    ).length
);

export const SELECT_DISBURSEMENT = createSelector(
  SELECT_TRANSACTION_STATE,
  (state: TransactionState): boolean => state.selectedDisbursement
);
